import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Paper,
  Button,
  InputAdornment,
} from '@mui/material';
import { ArrowUp, Chats, DotsThree } from '@phosphor-icons/react';
import { IFluxBotModel } from '../../../data/models/flux-bot';
import { IChatbotFlowModel } from '../../../data/models/chatbot';
import { FlowService } from '../../../service/FlowService';
import { flowToChatbotMapper } from '../../../utils/flow-to-chatbot-mapper';
import { useNotificationStore } from '../../../store/storeNotifications';
import { FluxService } from '../../../service/FluxService';
import { GroqService } from '../../../service/GroqService';
import { io, Socket } from 'socket.io-client';
import { generateRandomId } from '../../../utils/generateId';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { format, isValid, isToday, isYesterday } from 'date-fns';
import { Message } from './types';

interface ChatWidgetProps {
  chatbotData: IFluxBotModel;
  hideHeader?: boolean;
  onMenuAction?: (
    action:
      | 'playAudio'
      | 'toggleAutoAudio'
      | 'clearChat'
      | 'newChat'
      | 'changeSession',
    data?: any,
  ) => void;
  onGetChatbotState?: () => any;
}

const ChatWidget = React.forwardRef<any, ChatWidgetProps>(
  (
    { chatbotData, hideHeader = false, onMenuAction, onGetChatbotState },
    ref,
  ) => {
    const { setNotification } = useNotificationStore();
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState<Message[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [chatbot, setChatbot] = useState<IChatbotFlowModel | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [socketClient, setSocketClient] = useState<Socket | null>(null);
    const [socketId, setSocketId] = useState<string>('');
    const [sessionId, setSessionId] = useState<string>('');
    const [dropdownValues, setDropdownValues] = useState<string[]>([]);

    const chatAudioSpeakerRef = useRef<HTMLAudioElement>(null);
    const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(false);
    const [isChatSpeaking, setIsChatSpeaking] = useState(false);
    const [isGeneratingTTS, setIsGeneratingTTS] = useState(false);
    const [isLastChatResponseTTSGenerated, setIsLastChatResponseTTSGenerated] =
      useState(false);
    const [receivedStreamContentStorage, setReceivedStreamContentStorage] =
      useState<string>('');
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);

    useEffect(() => {
      const loadChatbot = async () => {
        try {
          const flowResponse = await FlowService.getFlow(chatbotData.id);
          const mappedChatbot = flowToChatbotMapper(flowResponse.data);

          const storageKey = `dashboardChatSession:${chatbotData.id}`;
          let chosenSessionId = localStorage.getItem(storageKey) || '';

          const hasStored = mappedChatbot.sessions?.some(
            s => s.id === chosenSessionId,
          );
          const hasPrimary = mappedChatbot.sessions?.some(
            s => s.id.toLowerCase() === 'primary',
          );

          if (!chosenSessionId || !hasStored) {
            if (hasPrimary) {
              chosenSessionId = mappedChatbot.sessions.find(
                s => s.id.toLowerCase() === 'primary',
              )!.id;
            } else if (
              mappedChatbot.sessions &&
              mappedChatbot.sessions.length > 0
            ) {
              chosenSessionId = mappedChatbot.sessions[0].id;
            } else {
              chosenSessionId = generateRandomId();
              try {
                mappedChatbot.sessions = mappedChatbot.sessions || [];
                mappedChatbot.sessions.push({
                  id: chosenSessionId,
                  name: chosenSessionId,
                  entries: [] as any,
                });
              } catch {}
            }
            localStorage.setItem(storageKey, chosenSessionId);
          }

          setSessionId(chosenSessionId);

          if (
            mappedChatbot.chatbotDropdowns &&
            mappedChatbot.chatbotDropdowns.length > 0
          ) {
            setDropdownValues(
              mappedChatbot.chatbotDropdowns.map(
                d => d.options?.[0]?.value ?? '',
              ),
            );
          } else {
            setDropdownValues([]);
          }
          setChatbot(mappedChatbot);

          try {
            const session = mappedChatbot.sessions?.find(
              s => s.id === chosenSessionId,
            );
            if (
              session &&
              Array.isArray(session.entries) &&
              session.entries.length > 0
            ) {
              const initMsgs: Message[] = session.entries.map((e: any) => ({
                role: e.role,
                content: e.content,
                timestamp: e.timestamp ?? new Date().toISOString(),
              }));
              setMessages(initMsgs);
            } else if (mappedChatbot.firstMessage) {
              setMessages([
                {
                  role: 'assistant',
                  content: mappedChatbot.firstMessage,
                  timestamp: new Date().toISOString(),
                },
              ]);
            } else {
              setMessages([]);
            }
          } catch {
            if (mappedChatbot.firstMessage) {
              setMessages([
                {
                  role: 'assistant',
                  content: mappedChatbot.firstMessage,
                  timestamp: new Date().toISOString(),
                },
              ]);
            }
          }
        } catch (error) {
          setNotification({
            type: 'error',
            message: 'Error loading chatbot',
          });
        }
      };

      loadChatbot();
    }, [chatbotData.id, setNotification]);

    useEffect(() => {
      const socket = io(import.meta.env.VITE_API_URL as string);

      socket.on('connect', () => {
        setSocketClient(socket);
        if (socket.id) setSocketId(socket.id);
      });

      socket.on('reconnect', attempt => {
        if (socket.id) setSocketId(socket.id);
      });

      socket.on('connect_error', (err: any) => {
        setNotification({
          type: 'error',
          message: 'Chat connection failed. Retrying…',
        });
      });

      socket.on('chatbot-req', async (resData: any) => {
        let generatedVoice: any = null;

        if (resData.data.type === 'stream') {
          const socketData = resData.data;

          switch (socketData.event) {
            case 'stream_start':
              setIsTyping(true);
              setShouldScrollToBottom(true);
              setMessages(prev => [
                ...prev,
                {
                  role: 'assistant',
                  content: '',
                  timestamp: socketData.data.timestamp,
                },
              ]);
              setReceivedStreamContentStorage('');
              break;
            case 'stream_error':
              break;
            case 'stream_thinking':
              break;
            case 'stream_message':
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage.role === 'assistant') {
                  lastMessage.content += socketData.data.text;
                }
                return newMessages;
              });
              setReceivedStreamContentStorage(
                old => old + socketData.data.text,
              );
              break;
            case 'stream_end':
              if (
                isAutoPlayEnabled &&
                chatbot?.chatbotVoice &&
                !hasAudioLink(receivedStreamContentStorage)
              ) {
                const voiceResponse = await generateVoiceBufferForStream();
                generatedVoice = voiceResponse;
                setIsLastChatResponseTTSGenerated(true);
              }
              setIsTyping(false);
              break;
            default:
              break;
          }
        } else {
          const item = resData?.data?.[0] ?? resData?.data?.data?.[0] ?? null;
          const text = item?.text ?? '';
          const timestamp = item?.timestamp ?? new Date().toISOString();

          if (text) {
            if (
              isAutoPlayEnabled &&
              chatbot?.chatbotVoice &&
              !hasAudioLink(text)
            ) {
              const voiceResponse = await GroqService.voiceGenerateBuffer(
                chatbot.chatbotVoice,
                {
                  text: text,
                  modelId: 'playai-tts',
                },
              );
              generatedVoice = voiceResponse;
              setIsLastChatResponseTTSGenerated(true);
            }

            setShouldScrollToBottom(true);
            setMessages(prev => [
              ...prev,
              { role: 'assistant', content: text, timestamp },
            ]);
          }
          setIsTyping(false);
        }

        if (generatedVoice) {
          handleSpeak(generatedVoice);
        }
      });

      return () => {
        socket?.off('connect');
        socket?.off('reconnect');
        socket?.off('connect_error');
        socket?.off('chatbot-req');
        socket?.disconnect();
        setSocketId('');
        setSocketClient(null);
      };
    }, [isAutoPlayEnabled, chatbot, setNotification]);

    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    };

    useEffect(() => {
      if (shouldScrollToBottom) {
        const timeoutId = setTimeout(() => {
          scrollToBottom();
        }, 50);

        return () => clearTimeout(timeoutId);
      }
    }, [messages, shouldScrollToBottom]);

    useEffect(() => {
      const audio = chatAudioSpeakerRef.current;
      if (!audio) return;

      const handleEnded = () => setIsChatSpeaking(false);
      const handlePause = () => setIsChatSpeaking(false);
      const handlePlay = () => setIsChatSpeaking(true);

      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('play', handlePlay);

      return () => {
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('play', handlePlay);
      };
    }, [chatAudioSpeakerRef.current]);

    useEffect(() => {
      if (!isAutoPlayEnabled) {
        chatAudioSpeakerRef.current?.pause();
      }
    }, [isAutoPlayEnabled]);

    useEffect(() => {
      setIsLastChatResponseTTSGenerated(false);
    }, [sessionId]);

    const handleSendMessage = async (
      e?: React.MouseEvent | React.FormEvent,
    ) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!inputValue.trim()) return;
      if (!chatbot) {
        setNotification({ type: 'error', message: 'Chatbot not loaded yet.' });
        return;
      }
      if (!socketClient || !socketId) {
        setNotification({
          type: 'warning',
          message: 'Connecting to chat… please try again in a moment.',
        });
        return;
      }
      if (!sessionId) {
        setNotification({
          type: 'warning',
          message: 'Setting up session… please try again in a moment.',
        });
        return;
      }

      const newMessage: Message = {
        role: 'user',
        content: inputValue.trim(),
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, newMessage]);
      setInputValue('');
      setIsTyping(true);
      setShouldScrollToBottom(true);

      try {
        await FluxService.chatApi(chatbot.id, socketId, {
          sessionId,
          variableInputs: [
            { inputText: newMessage.content },
            ...((chatbot.chatbotDropdowns ?? []).map((d, index) => ({
              inputId: d.varInputNodeId,
              inputText: dropdownValues[index],
            })) as any[]),
          ],
        });
      } catch (error) {
        setIsTyping(false);
        setNotification({
          type: 'error',
          message: 'Error sending message',
        });
      }
    };

    const handleSpeak = useCallback(
      (fileUrl: any | null) => {
        try {
          if (!fileUrl) {
            return;
          }

          if (chatAudioSpeakerRef.current && !isChatSpeaking) {
            const blob = new Blob([fileUrl.data], { type: 'audio/wav' });
            const audioUrl = URL.createObjectURL(blob);

            chatAudioSpeakerRef.current.src = audioUrl;
            chatAudioSpeakerRef.current.currentTime = 0;
            chatAudioSpeakerRef.current.play();
          }
        } catch (err) {}
      },
      [isChatSpeaking],
    );

    const handleGenerateTTS = useCallback(async () => {
      try {
        setIsGeneratingTTS(true);

        if (!chatbot || !sessionId || chatbot.sessions?.length === 0)
          return null;

        const currentSession = chatbot.sessions.find(s => s.id === sessionId);
        if (!currentSession || currentSession.entries.length === 0) return null;

        const lastChatResponse =
          currentSession.entries[currentSession.entries.length - 1].content;

        const voiceResponse = await GroqService.voiceGenerateBuffer(
          chatbot.chatbotVoice!,
          {
            text: lastChatResponse,
            modelId: 'playai-tts',
          },
        );

        return voiceResponse;
      } catch (err) {
        return null;
      } finally {
        setIsGeneratingTTS(false);
      }
    }, [chatbot, sessionId]);

    const handlePlayChatResponse = useCallback(async () => {
      if (isChatSpeaking) {
        chatAudioSpeakerRef.current?.pause();
        return;
      }

      if (!isLastChatResponseTTSGenerated) {
        const tts = await handleGenerateTTS();
        handleSpeak(tts);
        setIsLastChatResponseTTSGenerated(true);
        return;
      }

      chatAudioSpeakerRef.current!.currentTime = 0;
      chatAudioSpeakerRef.current?.play();
    }, [
      isChatSpeaking,
      isLastChatResponseTTSGenerated,
      handleGenerateTTS,
      handleSpeak,
    ]);

    const hasAudioLink = (text: string): boolean => {
      if (text.match(/\.(mp3|wav|ogg|m4a|aac|flac|webm)/i)) {
        return true;
      }
      return false;
    };

    const generateVoiceBufferForStream = async () => {
      if (!chatbot?.chatbotVoice) return null;

      const voiceResponse = await GroqService.voiceGenerateBuffer(
        chatbot.chatbotVoice,
        {
          text: receivedStreamContentStorage,
          modelId: 'playai-tts',
        },
      );

      return voiceResponse;
    };

    const handleVoiceToggle = useCallback(() => {
      setIsAutoPlayEnabled(!isAutoPlayEnabled);
    }, [isAutoPlayEnabled]);

    const handleClearConversation = useCallback(async () => {
      if (!chatbot) {
        setNotification({
          type: 'error',
          message: 'Chatbot not set',
        });
        return;
      }

      try {
        await FlowService.clearChatFlow(chatbot.id, sessionId);

        setChatbot(prev => {
          if (!prev) return prev;
          const updatedSessions = prev.sessions?.map(chatSession => {
            if (chatSession.id === sessionId) {
              return {
                ...chatSession,
                entries: [],
              };
            }
            return chatSession;
          });

          return { ...prev, sessions: updatedSessions };
        });

        setShouldScrollToBottom(false);
        setMessages([]);
        setIsLastChatResponseTTSGenerated(false);

        setTimeout(() => {
          setShouldScrollToBottom(true);
        }, 100);

        setNotification({
          type: 'success',
          message: 'Chat cleared successfully',
        });
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Error clearing chat',
        });
      }
      onMenuAction?.('clearChat');
    }, [chatbot, sessionId, setNotification, onMenuAction]);

    const handleCreateNewChat = useCallback(() => {
      const newSessionId = generateRandomId();

      const newChat = {
        id: newSessionId,
        name: newSessionId,
        entries:
          chatbot?.firstMessage && chatbot?.storeOpeningMessage !== 'as-title'
            ? [
                {
                  role: 'assistant' as const,
                  content: chatbot.firstMessage,
                  timestamp: new Date().toISOString(),
                  actions: chatbot.chatActions ?? [],
                },
              ]
            : [],
      };

      setSessionId(newSessionId);
      setMessages(
        newChat.entries.map(e => ({
          role: e.role,
          content: e.content,
          timestamp: e.timestamp,
        })),
      );

      if (chatbot) {
        setChatbot(prev => ({
          ...prev!,
          sessions: [...(prev?.sessions || []), newChat],
        }));
      }

      onMenuAction?.('newChat');
    }, [chatbot, onMenuAction]);

    const handleSessionChange = useCallback(
      (newSessionId: string) => {
        setSessionId(newSessionId);
        setIsLastChatResponseTTSGenerated(false);

        const storageKey = `dashboardChatSession:${chatbotData.id}`;
        localStorage.setItem(storageKey, newSessionId);

        if (chatbot) {
          const session = chatbot.sessions?.find(s => s.id === newSessionId);
          if (
            session &&
            Array.isArray(session.entries) &&
            session.entries.length > 0
          ) {
            const initMsgs: Message[] = session.entries.map((e: any) => ({
              role: e.role,
              content: e.content,
              timestamp: e.timestamp ?? new Date().toISOString(),
            }));
            setMessages(initMsgs);
          } else if (chatbot.firstMessage) {
            setMessages([
              {
                role: 'assistant',
                content: chatbot.firstMessage,
                timestamp: new Date().toISOString(),
              },
            ]);
          } else {
            setMessages([]);
          }
        }

        onMenuAction?.('changeSession', newSessionId);
      },
      [chatbot, chatbotData.id, onMenuAction],
    );

    const groupedSessionsByDate = useMemo(() => {
      if (!chatbot?.sessions || chatbot.sessions.length === 0) return {};

      const filteredChats = chatbot.sessions.filter(
        chat => chat.id !== 'default',
      );

      const grouped = filteredChats.reduce<Record<string, any[]>>(
        (acc, chat) => {
          if (!chat.entries || chat.entries.length === 0) {
            const dateKey = 'Today';
            acc[dateKey] = [...(acc[dateKey] || []), chat];
            return acc;
          }

          const lastEntryTimestamp =
            chat.entries[chat.entries.length - 1]?.timestamp;

          if (!isValid(Date.parse(lastEntryTimestamp))) {
            const dateKey = 'Today';
            acc[dateKey] = [...(acc[dateKey] || []), chat];
            return acc;
          }

          let dateKey: string;
          const parsedDate = Date.parse(lastEntryTimestamp);

          if (isToday(parsedDate)) {
            dateKey = 'Today';
          } else if (isYesterday(parsedDate)) {
            dateKey = 'Yesterday';
          } else {
            dateKey = format(parsedDate, 'MM/dd/yyyy');
          }

          acc[dateKey] = [...(acc[dateKey] || []), chat];
          return acc;
        },
        {},
      );

      return Object.fromEntries(
        Object.entries(grouped).sort(([a], [b]) => {
          if (a === 'Today') return -1;
          if (b === 'Today') return 1;
          if (a === 'Yesterday') return -1;
          if (b === 'Yesterday') return 1;
          return new Date(b).getTime() - new Date(a).getTime();
        }),
      );
    }, [chatbot?.sessions]);

    React.useImperativeHandle(
      ref,
      () => ({
        handlePlayChatResponse,
        handleVoiceToggle,
        handleClearConversation,
        handleCreateNewChat,
        handleSessionChange,
        getState: () => ({
          chatbot,
          messages,
          sessionId,
          isAutoPlayEnabled,
          isChatSpeaking,
          isGeneratingTTS,
          groupedSessionsByDate,
        }),
      }),
      [
        handlePlayChatResponse,
        handleVoiceToggle,
        handleClearConversation,
        handleCreateNewChat,
        handleSessionChange,
        chatbot,
        messages,
        sessionId,
        isAutoPlayEnabled,
        isChatSpeaking,
        isGeneratingTTS,
        groupedSessionsByDate,
      ],
    );

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: chatbot?.pageBackground || '#fff',
          color: chatbot?.pageText || '#000',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
          contain: 'layout style paint',
          fontFamily: chatbot?.font,
          fontWeight: chatbot?.fontWeight,
          backgroundImage: chatbot?.backgroundImage
            ? `url(${chatbot.backgroundImage})`
            : undefined,
          backgroundSize: chatbot?.backgroundImage ? 'cover' : undefined,
          backgroundPosition: chatbot?.backgroundImage ? 'center' : undefined,
          backgroundRepeat: chatbot?.backgroundImage ? 'no-repeat' : undefined,
        }}
        onKeyDown={e => e.stopPropagation()}
        onKeyUp={e => e.stopPropagation()}
        onKeyPress={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        <audio ref={chatAudioSpeakerRef} style={{ display: 'none' }} />
        {!chatbot ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography>Loading chatbot...</Typography>
          </Box>
        ) : (
          <>
            {!hideHeader && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderBottom: 'none',
                  backgroundColor: 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {chatbot.name}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => {}}
                    sx={{
                      color: chatbot.pageText || '#666',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                    }}
                    title="Chat options"
                  >
                    <DotsThree size={16} />
                  </IconButton>

                  {(chatbot.logo || chatbot.chatImage) && (
                    <Avatar
                      src={chatbot.logo ?? chatbot.chatImage ?? undefined}
                      alt={chatbot.name}
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius:
                          chatbot.iconShape === 'circle' ? '50%' : 0,
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                position: 'relative',
                overflowAnchor: 'none',
              }}
              onScroll={e => e.stopPropagation()}
            >
              {messages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent:
                      message.role === 'user' ? 'flex-end' : 'flex-start',
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      maxWidth: '80%',
                    }}
                  >
                    {message.role === 'assistant' && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius:
                            chatbot.iconShape === 'circle' ? '50%' : '4px',
                          padding: '2px',
                        }}
                      >
                        {chatbot.chatImage ? (
                          <Avatar
                            src={chatbot.chatImage}
                            alt={chatbot.name}
                            sx={{
                              width: `${chatbot.iconSize ?? 20}px`,
                              height: `${chatbot.iconSize ?? 20}px`,
                              borderRadius:
                                chatbot.iconShape === 'circle' ? '50%' : 0,
                            }}
                          />
                        ) : (
                          <Chats
                            color={chatbot.pageText || '#666'}
                            style={{
                              width: `${chatbot.iconSize ?? 20}px`,
                              height: `${chatbot.iconSize ?? 20}px`,
                            }}
                          />
                        )}
                      </Box>
                    )}

                    <Paper
                      sx={{
                        p: 1,
                        px: 2,
                        backgroundColor:
                          message.role === 'user'
                            ? chatbot.userContainer || '#3650DE'
                            : chatbot.responseContainer || '#f5f5f5',
                        color:
                          message.role === 'user'
                            ? chatbot.userText || '#fff'
                            : chatbot.responseText || '#000',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        wordBreak: 'break-word',
                      }}
                    >
                      {message.role === 'assistant' ? (
                        <div className={'chatbot-message-content'}>
                          <ReactMarkdown
                            rehypePlugins={[rehypeRaw, rehypeSanitize]}
                            remarkPlugins={[remarkGfm]}
                            components={{
                              img: ({ node, ...props }) => (
                                <img
                                  {...props}
                                  className="max-w-full my-2 rounded-md"
                                />
                              ),
                              audio: ({ node, ...props }) => (
                                <>
                                  <audio controls className="my-2">
                                    <track kind="captions" />
                                    <source src={props.src as any} />
                                    Your browser does not support the audio
                                    element.
                                  </audio>
                                  <br />
                                </>
                              ),
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <Typography variant="body2">
                          {message.content}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                </Box>
              ))}

              {isTyping && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius:
                          chatbot.iconShape === 'circle' ? '50%' : '4px',
                        padding: '2px',
                      }}
                    >
                      <Chats color={chatbot.pageText || '#666'} size={16} />
                    </Box>
                    <Paper
                      sx={{
                        p: 1,
                        px: 2,
                        backgroundColor: chatbot.responseContainer || '#f5f5f5',
                        borderRadius: '12px',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box
                          className="dot"
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: chatbot.responseText ?? '#666',
                            animation: 'typing 1.4s infinite',
                          }}
                        />
                        <Box
                          className="dot"
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: chatbot.responseText ?? '#666',
                            animation: 'typing 1.4s infinite',
                            animationDelay: '0.2s',
                          }}
                        />
                        <Box
                          className="dot"
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: chatbot.responseText ?? '#666',
                            animation: 'typing 1.4s infinite',
                            animationDelay: '0.4s',
                          }}
                        />
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            <Box
              sx={{ p: 2, borderTop: 'none' }}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              onMouseUp={e => e.stopPropagation()}
            >
              <form
                onSubmit={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSendMessage(e);
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  multiline
                  maxRows={4}
                  placeholder={chatbot.placeholder || 'Type your message...'}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      handleSendMessage(e);
                    }
                  }}
                  onFocus={e => e.stopPropagation()}
                  onBlur={e => e.stopPropagation()}
                  InputProps={{
                    sx: {
                      color: chatbot.responseText,
                      fontFamily: chatbot.font,
                      fontWeight: chatbot.fontWeight,
                      '-webkit-text-fill-color': chatbot.responseText,
                      background: chatbot.responseContainer,
                      borderRadius: '8px',
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          onClick={e => handleSendMessage(e as any)}
                          disabled={
                            !inputValue.trim() ||
                            isTyping ||
                            !socketId ||
                            !sessionId
                          }
                          sx={{
                            margin: 0,
                            padding: 0,
                            minWidth: 0,
                            minHeight: 0,
                            width: '24px',
                            height: '24px',
                            background: chatbot.brandColor || '#3650DE',
                            '&:hover': {
                              background: chatbot.brandColor || '#3650DE',
                            },
                          }}
                        >
                          <ArrowUp size={16} />
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    background: chatbot.responseContainer,
                    color: chatbot.responseText,
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: chatbot.inputBorderColor,
                    },
                    '& .MuiInputBase-root fieldset': {
                      borderColor: chatbot.inputBorderColor,
                    },
                    '& .MuiInputBase-root:hover fieldset': {
                      borderColor: chatbot.brandColor,
                    },
                    '& .MuiInputBase-root.Mui-focused fieldset': {
                      borderColor: chatbot.brandColor,
                    },
                  }}
                />
              </form>
            </Box>

            <style>
              {`
              @keyframes typing {
                0%, 100% {
                  transform: translateY(0);
                  opacity: 0.2;
                }
                50% {
                  transform: translateY(-2px);
                  opacity: 1;
                }
              }
            `}
            </style>
          </>
        )}
      </Box>
    );
  },
);

ChatWidget.displayName = 'ChatWidget';

export default ChatWidget;
